import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorReason, apiStatus, fetchIgnored } from './api';
import {
  IGNORED_REASON_ORDER,
  defaultIgnoredRange,
  ignoredQuery,
  ignoredReasonLabel,
  ignoredRowLabel,
  rangeError,
  studentProfilePath
} from './advisors';
import AssignAdvisorModal from './AssignAdvisorModal';
import type { AssignStudent } from './AssignAdvisorModal';
import { SHARED_STYLES } from '../styles';
import { Btn, Chip, LABEL_CLASS } from '../ui';
import StackTd from '../StackTable';
import { stackClasses } from '../stack_table';
import Stamp from './Stamp';
import type { DayRange } from './advisors';
import type { IgnoredList } from './types';
import type { Notify } from './AdvisorsPanel';

interface Props {
  notify: Notify;
  initialRange?: DayRange;
  // Reports an applied range so the page can keep it in the URL.
  onRangeChange?: (range: DayRange) => void;
  // Owned by the panel so the marker survives toggling views while the Advisors tab is open.
  assigned: ReadonlySet<string>;
  onAssigned: (studentUuid: string) => void;
  // can_write_advisors; without it the list is read-only (no Assign buttons or modal).
  canWrite: boolean;
}

const { FIELD } = SHARED_STYLES;

const COLS = [
  { label: 'When', width: 'w-[16%]' },
  { label: 'Student', width: 'w-[22%]' },
  { label: 'Tagged by', width: 'w-[18%]' },
  { label: 'Why nobody was assigned', width: '' },
  { label: '', width: 'w-[140px]' }
];
const CELL = 'px-3.5 py-2.5 text-left text-[13px] font-normal';
const LABEL = 'text-xs text-content-muted';
const ST = stackClasses();
// Rows whose tag found an advisor already: Reassign is the path there, not Assign.
const ALREADY_ASSIGNED_REASON = 'ignored_already_assigned';
const EMPTY: IgnoredList = { rows: [], summary: { by_reason: {}, by_author: [] }, next_cursor: 0 };

// Notes whose tag did not assign an advisor (REQ 51), by IST day range.
function IgnoredView({
  notify,
  initialRange,
  onRangeChange,
  assigned,
  onAssigned,
  canWrite
}: Props) {
  const [draft, setDraft] = useState<DayRange>(initialRange || defaultIgnoredRange());
  const [applied, setApplied] = useState<DayRange>(draft);
  const [data, setData] = useState<IgnoredList>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<{ forbidden: boolean; reason: string } | null>(null);
  const [assignTarget, setAssignTarget] = useState<AssignStudent | null>(null);
  const done = (uuid: string) => assigned.has(uuid);
  const loadSeq = useRef(0);
  const draftError = rangeError(draft);

  // Read-only callers, and rows whose tag found an advisor already, get no action.
  const renderAction = (r: IgnoredList['rows'][number]) => {
    if (!canWrite || r.reason === ALREADY_ASSIGNED_REASON) return null;
    if (done(r.student_uuid)) {
      return <span className="text-xs font-medium text-state-success-text">Assigned</span>;
    }
    return (
      <Btn
        variant="neutral"
        size="row"
        onClick={() =>
          setAssignTarget({ uuid: r.student_uuid, name: r.student_name || r.student_uuid })
        }
        aria-label={`Assign advisor to ${r.student_name || r.student_uuid}`}
      >
        Assign advisor
      </Btn>
    );
  };

  const load = useCallback(
    (range: DayRange) => {
      loadSeq.current += 1;
      const seq = loadSeq.current;
      setLoading(true);
      return fetchIgnored(ignoredQuery(range))
        .then((d) => {
          if (seq !== loadSeq.current) return;
          setData(d);
          setLoadError(null);
        })
        .catch((e: unknown) => {
          if (seq !== loadSeq.current) return;
          const forbidden = apiStatus(e) === 403;
          const reason = forbidden
            ? 'Super user or sales manager only'
            : apiErrorReason(e, 'Failed to load the list');
          // Drop the previous range's rows so they never sit under the new dates.
          setData(EMPTY);
          setLoadError({ forbidden, reason });
          if (!forbidden) notify('error', reason);
        })
        .finally(() => {
          if (seq === loadSeq.current) setLoading(false);
        });
    },
    [notify]
  );

  useEffect(() => {
    load(applied);
  }, [load, applied]);

  const loadMore = () => {
    if (!data.next_cursor || loadingMore) return;
    const seq = loadSeq.current;
    setLoadingMore(true);
    fetchIgnored(ignoredQuery(applied, data.next_cursor))
      .then((d) => {
        if (seq !== loadSeq.current) return;
        setData((prev) => ({
          ...prev,
          rows: [...prev.rows, ...d.rows],
          next_cursor: d.next_cursor
        }));
      })
      .catch((e: unknown) => notify('error', apiErrorReason(e, 'Failed to load more')))
      .finally(() => setLoadingMore(false));
  };

  const apply = (range: DayRange) => {
    setDraft(range);
    setApplied(range);
    onRangeChange?.(range);
  };

  const { by_reason: byReason, by_author: byAuthor } = data.summary;

  return (
    <div aria-label="Not assigned">
      <form
        className="flex flex-row flex-wrap items-end justify-start gap-2.5 border-b border-line-subtle px-3.5 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!draftError) apply({ ...draft });
        }}
      >
        <label htmlFor="ignored-from" className={LABEL}>
          From (IST)
          <input
            id="ignored-from"
            type="date"
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            className="block text-sm"
            style={FIELD}
          />
        </label>
        <label htmlFor="ignored-to" className={LABEL}>
          To (IST)
          <input
            id="ignored-to"
            type="date"
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            className="block text-sm"
            style={FIELD}
          />
        </label>
        <Btn type="submit" variant="primary" disabled={!!draftError}>
          Apply
        </Btn>
        <Btn
          variant="neutral"
          onClick={() => {
            apply(defaultIgnoredRange());
          }}
        >
          Last 7 days
        </Btn>
        {draftError ? (
          <span role="alert" className="text-xs" style={{ color: 'var(--state-error)' }}>
            {draftError}
          </span>
        ) : null}
      </form>

      {loadError && data.rows.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <div role="alert" className="text-[13px]" style={{ color: 'var(--state-error)' }}>
            {loadError.forbidden
              ? loadError.reason
              : `Could not load the list: ${loadError.reason}`}
          </div>
          {loadError.forbidden ? null : (
            <Btn
              variant="outline"
              size="row"
              onClick={() => load(applied)}
              disabled={loading}
              style={{ marginTop: 12 }}
            >
              {loading ? 'Retrying…' : 'Retry'}
            </Btn>
          )}
        </div>
      ) : (
        <>
          <div
            className="border-b border-line-subtle px-3.5 py-3 text-[13px]"
            role="region"
            aria-label="Not assigned summary"
          >
            <div className="flex flex-wrap gap-2">
              {IGNORED_REASON_ORDER.map((r) => (
                <Chip key={r} tone="warning">
                  {`${ignoredReasonLabel(r)}: ${byReason[r] || 0}`}
                </Chip>
              ))}
            </div>
            {byAuthor.length > 0 ? (
              <p className="m-0 mt-2 text-xs text-content-muted">
                {`By author: ${byAuthor.map((a) => `${a.name || 'unknown'} ${a.n}`).join(', ')}`}
              </p>
            ) : null}
          </div>
          <table className={ST.table} aria-label="Notes that assigned nobody">
            <thead className={`border-b border-line-subtle bg-surface-sunken ${ST.head}`}>
              <tr>
                {COLS.map((c) => (
                  <th
                    key={c.label || 'action'}
                    className={`px-3.5 py-2 text-left ${LABEL_CLASS} ${c.width}`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={ST.body}>
              {data.rows.length === 0 ? (
                <tr className={ST.row}>
                  <StackTd
                    colSpan={COLS.length}
                    className="px-4 py-8 text-center text-content-muted"
                  >
                    {loading ? 'Loading…' : 'No ignored tags in this range.'}
                  </StackTd>
                </tr>
              ) : (
                data.rows.map((r) => (
                  <tr key={r.id} className={`border-t border-line-subtle text-[12.5px] ${ST.row}`}>
                    <StackTd label="When" className={CELL}>
                      <Stamp iso={r.note_created_date} />
                    </StackTd>
                    <StackTd
                      label="Student"
                      className={`${CELL} truncate`}
                      title={r.student_name || r.student_uuid}
                    >
                      <a
                        href={studentProfilePath(r.student_uuid)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-content-link hover:underline"
                      >
                        {r.student_name || r.student_uuid}
                      </a>
                    </StackTd>
                    <StackTd
                      label="Tagged by"
                      className={`${CELL} truncate`}
                      title={r.author_name || undefined}
                    >
                      {r.author_name || 'unknown'}
                    </StackTd>
                    <StackTd label="Why nobody was assigned" className={CELL}>
                      {ignoredRowLabel(r.reason, r.detail, r.detail_name)}
                    </StackTd>
                    <StackTd className={CELL}>{renderAction(r)}</StackTd>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {data.next_cursor > 0 ? (
            <div className="border-t border-line-subtle px-3.5 py-3">
              <Btn variant="neutral" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Btn>
            </div>
          ) : null}
        </>
      )}
      <AssignAdvisorModal
        student={canWrite ? assignTarget : null}
        notify={notify}
        onClose={() => setAssignTarget(null)}
        onDone={() => {
          // Covers a 409 too: either way the student now has an advisor.
          if (assignTarget) onAssigned(assignTarget.uuid);
          load(applied);
        }}
      />
    </div>
  );
}

export default IgnoredView;
